import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  VStack,
  Badge,
  Spinner,
  useToast,
  Image,
  Link,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Input,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { RefreshCw, ExternalLink, Calendar, Image as ImageIcon, Eye, Plus } from 'lucide-react';
import AdminLayout from '../../layouts/AdminLayout';
import api, { API_URL } from '../../services/api';
import { getZoneramaManifestWithFallbacks } from '../../services/zonerama';

interface Album {
  id: string;
  title: string;
  url: string;
  date: string;
  photos_count: number;
  views_count?: number;
  photos: Array<{
    id: string;
    page_url: string;
    image_1500: string;
  }>;
}

const resolveBackendUrl = (path: string) => {
  try {
    if (/^https?:\/\//i.test(path)) return path;
    if (path.startsWith('/cache') || path.startsWith('/uploads') || path.startsWith('/api/')) {
      let origin = '';
      try {
        const assetBase = (process.env.REACT_APP_ASSET_BASE_URL || '').trim();
        if (assetBase) {
          origin = new URL(assetBase, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
        }
      } catch {}
      if (!origin) {
        try {
          origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
        } catch {}
      }
      const abs = new URL(path, origin || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'));
      return abs.toString();
    }
    return path;
  } catch {
    return path;
  }
};

const GalleryAdminPage: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string>('');
  const toast = useToast();
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();
  const [albumUrl, setAlbumUrl] = useState<string>('');
  const [photoLimit, setPhotoLimit] = useState<number>(50);
  const [adding, setAdding] = useState<boolean>(false);

  const fetchAlbums = async (): Promise<Album[]> => {
    setLoading(true);
    setError('');
    
    try {
      // Try to load from both sources and combine them (similar to frontpage)
      const [profileRes, albumsRes] = await Promise.allSettled([
        fetch(resolveBackendUrl('/cache/prefetch/zonerama_profile.json'), { cache: 'no-cache' }),
        fetch(resolveBackendUrl('/cache/prefetch/zonerama_albums.json'), { cache: 'no-cache' })
      ]);
      
      let combinedAlbums: Album[] = [];
      
      // Get profile albums (main source)
      if (profileRes.status === 'fulfilled' && profileRes.value.ok) {
        const profileData = await profileRes.value.json();
        if (profileData.albums && Array.isArray(profileData.albums)) {
          combinedAlbums = [...profileData.albums];
        }
      }
      
      // Get additional albums (fallback source)
      if (albumsRes.status === 'fulfilled' && albumsRes.value.ok) {
        const albumsData = await albumsRes.value.json();
        const blogAlbums = Array.isArray(albumsData) ? albumsData : [];
        
        // Filter out empty albums and avoid duplicates
        const validBlogAlbums = blogAlbums.filter((album: any) => 
          album.id && 
          album.title && 
          !combinedAlbums.some(existing => existing.id === album.id)
        );
        
        combinedAlbums = [...combinedAlbums, ...validBlogAlbums];
      }
      // Fallback: synthesize albums from flat manifest when both sources fail/empty
      if (!combinedAlbums || combinedAlbums.length === 0) {
        try {
          const items = await getZoneramaManifestWithFallbacks();
          if (Array.isArray(items) && items.length > 0) {
            const byAlbum: Record<string, any[]> = {} as any;
            items.forEach((it: any) => {
              const aid = String(it.album_id || 'unknown');
              (byAlbum[aid] = byAlbum[aid] || []).push(it);
            });
            const synthesized: Album[] = Object.entries(byAlbum).map(([aid, arr]) => ({
              id: aid,
              title: 'Album',
              url: (arr[0] as any).page_url || '#',
              date: '',
              photos_count: (arr as any[]).length,
              photos: (arr as any[]).slice(0, 12).map((p: any) => ({ id: String(p.id || ''), page_url: String(p.page_url || ''), image_1500: String(p.src || p.local || '') })),
            }));
            combinedAlbums = synthesized;
          }
        } catch {}
      }

      // Sort albums by date (desc). Support both ISO (yyyy-mm-dd) and Czech (dd.mm.yyyy) formats
      const parseDate = (s: string): number => {
        const str = String(s || '').trim();
        if (!str) return 0;
        // Try ISO
        const iso = Date.parse(str);
        if (!Number.isNaN(iso)) return iso;
        // Try dd.mm.yyyy
        const m = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
        if (m) {
          const d = parseInt(m[1], 10);
          const mo = parseInt(m[2], 10) - 1;
          const y = parseInt(m[3], 10);
          return new Date(y, mo, d).getTime();
        }
        return 0;
      };
      combinedAlbums = [...combinedAlbums].sort((a, b) => {
        const da = parseDate(a.date);
        const db = parseDate(b.date);
        if (db !== da) return db - da;
        // Stable-ish fallback: keep deterministic order by id if dates equal/missing
        return String(b.id || '').localeCompare(String(a.id || ''));
      });

      setAlbums(combinedAlbums);
      return combinedAlbums;
    } catch (err: any) {
      setError(err.message || 'Nepodařilo se načíst alba');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    
    try {
      // Use the api service which automatically includes authentication
      await api.post('/admin/gallery/refresh', {});

      toast({
        title: 'Galerie obnovena',
        description: 'Data z Zonerama byla úspěšně načtena',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reload albums after refresh with short polling (refresh runs async on server)
      let loaded: Album[] = [];
      for (let i = 0; i < 5; i++) {
        // small delay before each attempt to allow backend to finish
        await new Promise((r) => setTimeout(r, 1200));
        loaded = await fetchAlbums();
        if (loaded && loaded.length > 0) break;
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Nepodařilo se obnovit galerii';
      
      toast({
        title: 'Chyba při obnově galerie',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      console.error('Gallery refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddAlbum = async () => {
    const link = albumUrl.trim();
    if (!link || !link.includes('/Album/')) {
      toast({
        title: 'Neplatný odkaz',
        description: 'URL musí obsahovat "/Album/"',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setAdding(true);
    try {
      const limit = Number.isFinite(photoLimit) && photoLimit > 0 ? photoLimit : 50;
      await api.post('/admin/gallery/albums/fetch', { link, photo_limit: limit });
      toast({
        title: 'Album přidáno',
        description: 'Album bylo načteno a uloženo.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onAddClose();
      setAlbumUrl('');
      await fetchAlbums();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.message || 'Nepodařilo se přidat album';
      toast({
        title: 'Chyba při přidání alba',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, []);

  const totalPhotos = albums.reduce((sum, album) => sum + album.photos_count, 0);
  const totalViews = albums.reduce((sum, album) => sum + (album.views_count || 0), 0);

  return (
    <AdminLayout requireAdmin={false}>
      <Container maxW="7xl" py={8}>
        <VStack align="stretch" spacing={6}>
          {/* Header */}
          <HStack justify="space-between" align="center" flexWrap="wrap">
            <VStack align="start" spacing={1}>
              <Heading size="xl">Správa galerie</Heading>
              <Text color="gray.600">
                Správa alb a fotografií ze Zonerama
              </Text>
            </VStack>

            <HStack spacing={3}>
              <Button
                leftIcon={<Plus size={18} />}
                colorScheme="green"
                onClick={onAddOpen}
              >
                Přidat album
              </Button>
              <Button
                leftIcon={<RefreshCw size={18} />}
                colorScheme="blue"
                onClick={handleRefresh}
                isLoading={refreshing}
                loadingText="Obnova..."
              >
                Obnovit z Zonerama
              </Button>
            </HStack>
          </HStack>

          {/* Zonerama Info */}
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Zonerama integrace</AlertTitle>
              <AlertDescription>
                Alba jsou automaticky načítána ze Zonerama profilu. Klikněte na "Obnovit z Zonerama" pro synchronizaci s nejnovějšími daty.
              </AlertDescription>
            </Box>
          </Alert>

          {/* Statistics */}
          {!loading && !error && albums.length > 0 && (
            <HStack spacing={4} flexWrap="wrap">
              <Badge colorScheme="purple" fontSize="md" p={3} borderRadius="md">
                {albums.length} alb
              </Badge>
              <Badge colorScheme="blue" fontSize="md" p={3} borderRadius="md">
                {totalPhotos} fotografií
              </Badge>
              <Badge colorScheme="green" fontSize="md" p={3} borderRadius="md">
                {totalViews} zhlédnutí
              </Badge>
            </HStack>
          )}

          {/* Loading State */}
          {loading && (
            <VStack spacing={4} py={12}>
              <Spinner size="xl" color="brand.primary" />
              <Text color="gray.600">Načítám alba...</Text>
            </VStack>
          )}

          {/* Error State */}
          {error && !loading && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Empty State */}
          {!loading && !error && albums.length === 0 && (
            <Box
              bg={cardBg}
              borderWidth="1px"
              borderRadius="lg"
              p={12}
              textAlign="center"
            >
              <VStack spacing={4}>
                <ImageIcon size={48} color="gray" />
                <Heading size="md" color="gray.600">
                  Zatím nejsou k dispozici žádná alba
                </Heading>
                <Text color="gray.500">
                  Klikněte na tlačítko "Obnovit z Zonerama" pro načtení alb.
                </Text>
                <Button
                  leftIcon={<RefreshCw size={18} />}
                  colorScheme="blue"
                  onClick={handleRefresh}
                  isLoading={refreshing}
                >
                  Obnovit z Zonerama
                </Button>
              </VStack>
            </Box>
          )}

          {/* Albums Table */}
          {!loading && !error && albums.length > 0 && (
            <Box
              bg={cardBg}
              borderWidth="1px"
              borderRadius="lg"
              overflow="hidden"
              boxShadow="sm"
            >
              <Table variant="simple">
                <Thead bg={useColorModeValue('gray.50', 'gray.900')}>
                  <Tr>
                    <Th width="100px">Náhled</Th>
                    <Th>Název</Th>
                    <Th width="120px">Datum</Th>
                    <Th width="100px" isNumeric>Fotky</Th>
                    <Th width="120px" isNumeric>Zhlédnutí</Th>
                    <Th width="180px">Akce</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {albums.map((album) => {
                    const coverPhoto = album.photos && album.photos.length > 0 
                      ? album.photos[0] 
                      : null;

                    return (
                      <Tr key={album.id} _hover={{ bg: 'gray.50' }}>
                        <Td>
                          {coverPhoto ? (
                            <Image
                              src={resolveBackendUrl(coverPhoto.image_1500)}
                              alt={album.title}
                              boxSize="60px"
                              objectFit="cover"
                              borderRadius="md"
                            />
                          ) : (
                            <Box
                              boxSize="60px"
                              bg="gray.200"
                              borderRadius="md"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <ImageIcon size={24} color="gray" />
                            </Box>
                          )}
                        </Td>
                        <Td>
                          <Text fontWeight="600" color="gray.800" noOfLines={2}>
                            {album.title}
                          </Text>
                        </Td>
                        <Td>
                          <HStack spacing={1} fontSize="sm" color="gray.600">
                            <Calendar size={14} />
                            <Text>{album.date}</Text>
                          </HStack>
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme="blue">
                            {album.photos_count}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          <HStack spacing={1} justify="flex-end">
                            <Eye size={14} />
                            <Text fontSize="sm">{album.views_count || 0}</Text>
                          </HStack>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Button
                              as={Link}
                              href={`/galerie/album/${album.id}`}
                              target="_blank"
                              size="sm"
                              colorScheme="purple"
                              variant="outline"
                            >
                              Náhled
                            </Button>
                            <Button
                              as={Link}
                              href={album.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              rightIcon={<ExternalLink size={14} />}
                            >
                              Zonerama
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
          <Modal isOpen={isAddOpen} onClose={onAddClose} size="lg">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Přidat Zonerama album</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="stretch" spacing={4}>
                  <FormControl>
                    <FormLabel>URL Zonerama alba</FormLabel>
                    <Input
                      placeholder="https://eu.zonerama.com/…/Album/12345"
                      value={albumUrl}
                      onChange={(e) => setAlbumUrl(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Limit fotek</FormLabel>
                    <Input
                      type="number"
                      min={1}
                      max={200}
                      value={String(photoLimit)}
                      onChange={(e) => {
                        const v = parseInt(e.target.value || '0', 10);
                        setPhotoLimit(Number.isFinite(v) ? v : 50);
                      }}
                    />
                  </FormControl>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <HStack spacing={3}>
                  <Button variant="ghost" onClick={onAddClose}>Zrušit</Button>
                  <Button colorScheme="blue" onClick={handleAddAlbum} isLoading={adding}>Načíst album</Button>
                </HStack>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </VStack>
      </Container>
    </AdminLayout>
  );
};

export default GalleryAdminPage;
